import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Select,
  DatePicker,
  Button,
  Space,
  Typography,
  Table,
  Tag,
  message,
  Spin,
  Empty,
} from 'antd';
import {
  DollarOutlined,
  DownloadOutlined,
  FilePdfOutlined,
  FileExcelOutlined,
  PieChartOutlined,
  BarChartOutlined,
  LineChartOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, LineChart, Line } from 'recharts';
import dayjs from 'dayjs';
import { useParams } from 'react-router-dom';
import { getExpenseApi, getExpenseStatsApi } from '../../services/expenseApi';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const ExpenseReport = () => {
  const [loading, setLoading] = useState(false);
  const [reportPeriod, setReportPeriod] = useState('monthly'); // daily, weekly, monthly, custom
  const [dateRange, setDateRange] = useState([null, null]);
  const [statsData, setStatsData] = useState(null);
  const [expenseList, setExpenseList] = useState([]);
  const { companyId,tenantId } = useParams();

  // Colors for charts
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82ca9d', '#ffc658', '#ff7c7c'];

  useEffect(() => {
    fetchReportData();
  }, [reportPeriod, dateRange]);

  // Calculate date range based on period
  const getDateRangeForPeriod = () => {
    const today = dayjs();
    let startDate, endDate;

    switch (reportPeriod) {
      case 'daily':
        startDate = today.startOf('day');
        endDate = today.endOf('day');
        break;
      case 'weekly':
        startDate = today.startOf('week');
        endDate = today.endOf('week');
        break;
      case 'monthly':
        startDate = today.startOf('month');
        endDate = today.endOf('month');
        break;
      case 'custom':
        if (dateRange[0] && dateRange[1]) {
          startDate = dateRange[0];
          endDate = dateRange[1];
        } else {
          return null;
        }
        break;
      default:
        startDate = today.startOf('month');
        endDate = today.endOf('month');
    }

    return { startDate, endDate };
  };

  // Fetch report data
  const fetchReportData = async () => {
    const dateRangeObj = getDateRangeForPeriod();
    if (!dateRangeObj) return;

    try {
      setLoading(true);

      const params = {
        companyId: companyId,
        tenantId:tenantId,
        startDate: dateRangeObj.startDate.format('YYYY-MM-DD'),
        endDate: dateRangeObj.endDate.format('YYYY-MM-DD'),
      };

      // Fetch statistics
      const statsResponse = await getExpenseStatsApi(params);
      if (statsResponse.success) {
        setStatsData(statsResponse.data);
      }

      // Fetch expense list for table
      const expenseParams = {
        ...params,
        page: 1,
        limit: 1000, // Get all expenses for the period
        sortBy: 'date',
        sortOrder: 'desc',
      };
      const expenseResponse = await getExpenseApi(expenseParams);
      if (expenseResponse?.success) {
        setExpenseList(expenseResponse.data);
      }
    } catch (error) {
      message.error('Failed to fetch report data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Handle period change
  const handlePeriodChange = (value) => {
    setReportPeriod(value);
    if (value !== 'custom') {
      setDateRange([null, null]);
    }
  };

  // Handle custom date range change
  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    if (dates && dates[0] && dates[1]) {
      setReportPeriod('custom');
    }
  };

  // Download CSV
  const downloadCSV = () => {
    if (expenseList.length === 0) {
      message.warning('No data to download');
      return;
    }

    const headers = ['Date', 'Expense Name', 'Category', 'Amount', 'Payment Mode', 'Notes'];
    const csvContent = [
      headers.join(','),
      ...expenseList.map(expense => [
        dayjs(expense.date).format('DD/MM/YYYY'),
        `"${expense.expenseName}"`,
        expense.category,
        expense.amount,
        expense.paymentMode,
        `"${expense.notes || ''}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `expense_report_${dayjs().format('YYYY-MM-DD')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    message.success('CSV downloaded successfully');
  };

  // Download PDF (using window.print as simple solution)
  const downloadPDF = () => {
    window.print();
    message.success('Print dialog opened. Save as PDF from print options.');
  };

  // Format currency
  const formatCurrency = (amount) => {
    return `₹${amount?.toLocaleString('en-IN') || 0}`;
  };

  // Prepare data for pie chart
  const categoryChartData = statsData?.byCategory?.map(item => ({
    name: item._id,
    value: item.total,
    count: item.count
  })) || [];

  // Prepare data for payment mode chart
  const paymentModeChartData = statsData?.byPaymentMode?.map(item => ({
    name: item._id,
    value: item.total,
    count: item.count
  })) || [];

  // Table columns
  const columns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (date) => dayjs(date).format('DD/MM/YYYY'),
      width: 120,
    },
    {
      title: 'Expense Name',
      dataIndex: 'expenseName',
      key: 'expenseName',
      width: 200,
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 150,
      render: (category) => <Tag color="blue">{category}</Tag>,
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      render: (amount) => formatCurrency(amount),
    },
    {
      title: 'Payment Mode',
      dataIndex: 'paymentMode',
      key: 'paymentMode',
      width: 130,
      render: (mode) => <Tag color="green">{mode}</Tag>,
    },
  ];

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{ 
          backgroundColor: 'white', 
          padding: '10px', 
          border: '1px solid #ccc',
          borderRadius: '4px'
        }}>
          <p style={{ margin: 0, fontWeight: 'bold' }}>{payload[0].name}</p>
          <p style={{ margin: '5px 0 0 0', color: '#1890ff' }}>
            Amount: {formatCurrency(payload[0].value)}
          </p>
          <p style={{ margin: '5px 0 0 0' }}>
            Count: {payload[0].payload.count}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div style={{ padding: '24px', backgroundColor: '#f0f2f5', minHeight: '100vh' }}>
      {/* Header */}
      <Card style={{ marginBottom: 24 }}>
        <Row align="middle" justify="space-between">
          <Col>
            <Space direction="vertical" size={0}>
              <Title level={3} style={{ margin: 0 }}>
                <BarChartOutlined /> Expense Report
              </Title>
              <Text type="secondary">View and analyze your expenses</Text>
            </Space>
          </Col>
          <Col>
            <Space>
              <Select
                value={reportPeriod}
                onChange={handlePeriodChange}
                style={{ width: 150 }}
                size="large"
              >
                <Option value="daily">Daily</Option>
                <Option value="weekly">Weekly</Option>
                <Option value="monthly">Monthly</Option>
                <Option value="custom">Custom Range</Option>
              </Select>

              {reportPeriod === 'custom' && (
                <RangePicker
                  value={dateRange}
                  onChange={handleDateRangeChange}
                  format="DD/MM/YYYY"
                  size="large"
                />
              )}

              <Button
                type="primary"
                icon={<FileExcelOutlined />}
                onClick={downloadCSV}
                size="large"
                disabled={expenseList.length === 0}
              >
                CSV
              </Button>
              <Button
                type="default"
                icon={<FilePdfOutlined />}
                onClick={downloadPDF}
                size="large"
                disabled={expenseList.length === 0}
              >
                PDF
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <Spin spinning={loading}>
        {/* Statistics Cards */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Total Expenses"
                value={statsData?.overall?.totalExpenses || 0}
                prefix={<CalendarOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Total Amount"
                value={statsData?.overall?.totalAmount || 0}
                prefix="₹"
                precision={2}
                valueStyle={{ color: '#3f8600' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Average Amount"
                value={statsData?.overall?.avgAmount || 0}
                prefix="₹"
                precision={2}
                valueStyle={{ color: '#cf1322' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Highest Expense"
                value={statsData?.overall?.maxAmount || 0}
                prefix="₹"
                precision={2}
                valueStyle={{ color: '#faad14' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Charts */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          {/* Category Breakdown - Pie Chart */}
          <Col xs={24} lg={12}>
            <Card 
              title={
                <Space>
                  <PieChartOutlined />
                  <span>Expense by Category</span>
                </Space>
              }
              style={{ height: '100%' }}
            >
              {categoryChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={categoryChartData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {categoryChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Empty description="No data available" />
              )}
            </Card>
          </Col>

          {/* Payment Mode Breakdown - Bar Chart */}
          <Col xs={24} lg={12}>
            <Card 
              title={
                <Space>
                  <BarChartOutlined />
                  <span>Expense by Payment Mode</span>
                </Space>
              }
              style={{ height: '100%' }}
            >
              {paymentModeChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={paymentModeChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="value" fill="#8884d8">
                      {paymentModeChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Empty description="No data available" />
              )}
            </Card>
          </Col>
        </Row>

        {/* Category Breakdown Table */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col xs={24} lg={12}>
            <Card title="Category Summary">
              <Table
                dataSource={categoryChartData}
                columns={[
                  {
                    title: 'Category',
                    dataIndex: 'name',
                    key: 'name',
                    render: (text) => <Tag color="blue">{text}</Tag>,
                  },
                  {
                    title: 'Count',
                    dataIndex: 'count',
                    key: 'count',
                    align: 'center',
                  },
                  {
                    title: 'Total Amount',
                    dataIndex: 'value',
                    key: 'value',
                    render: (value) => formatCurrency(value),
                    align: 'right',
                  },
                ]}
                pagination={false}
                size="small"
                rowKey="name"
              />
            </Card>
          </Col>

          <Col xs={24} lg={12}>
            <Card title="Payment Mode Summary">
              <Table
                dataSource={paymentModeChartData}
                columns={[
                  {
                    title: 'Payment Mode',
                    dataIndex: 'name',
                    key: 'name',
                    render: (text) => <Tag color="green">{text}</Tag>,
                  },
                  {
                    title: 'Count',
                    dataIndex: 'count',
                    key: 'count',
                    align: 'center',
                  },
                  {
                    title: 'Total Amount',
                    dataIndex: 'value',
                    key: 'value',
                    render: (value) => formatCurrency(value),
                    align: 'right',
                  },
                ]}
                pagination={false}
                size="small"
                rowKey="name"
              />
            </Card>
          </Col>
        </Row>

        {/* Detailed Expense List */}
        <Card title="Detailed Expense List">
          <Table
            columns={columns}
            dataSource={expenseList}
            rowKey="_id"
            pagination={{
              pageSize: 10,
              showTotal: (total) => `Total ${total} expenses`,
              showSizeChanger: true,
            }}
            scroll={{ x: 800 }}
            summary={(pageData) => {
              const total = pageData.reduce((sum, expense) => sum + expense.amount, 0);
              return (
                <Table.Summary fixed>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={3}>
                      <strong>Total (Current Page)</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1}>
                      <strong>{formatCurrency(total)}</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2} />
                  </Table.Summary.Row>
                </Table.Summary>
              );
            }}
          />
        </Card>
      </Spin>

      {/* Print styles */}
      <style jsx>{`
        @media print {
          .ant-card {
            page-break-inside: avoid;
          }
          button {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default ExpenseReport;